import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Users, DollarSign, Phone, TrendingUp, Clock, MessageSquare, ArrowUpRight, ArrowDownRight, Search, Activity, Shield, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';

export default function AdminPage() {
  const { api, user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditKind, setAuditKind] = useState('impersonation_start');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/overview');
        setData(data);
        setError('');
      } catch (e) {
        console.error('admin overview failed', e);
        const status = e?.response?.status;
        setError(status ? `/admin/overview returned ${status} — ${e.response?.data?.detail || 'no detail'}` : (e.message || 'Network error'));
      }
    })();
  }, [api]);

  // Lazy-load Health when its tab is opened.
  useEffect(() => {
    if (tab !== 'health' || health !== null) return;
    refreshHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Lazy-load Audit when its tab is opened OR when filter changes.
  useEffect(() => {
    if (tab !== 'audit') return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/audit-log?kind=${encodeURIComponent(auditKind)}&limit=100`);
        setAudit(data);
      } catch {}
    })();
  }, [tab, auditKind, api]);

  const refreshHealth = async () => {
    setHealthLoading(true);
    try {
      const { data } = await api.get('/admin/health');
      setHealth(data);
    } catch {}
    finally { setHealthLoading(false); }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f8f4]">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-semibold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-500 text-sm">Super admin access required.</p>
          <Link to="/dashboard" className="text-blue-600 text-sm mt-4 inline-block">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const stats = data?.stats || { total: 0, active: 0, calls: 0, mrr: 0,
                                  total_cost: 0, vapi_cost: 0, twilio_cost: 0, openai_cost: 0 };
  const clients = data?.clients || [];
  const users = data?.users || [];

  // Per-user usage roll-ups (sum across the user list). Minutes/SMS come from
  // the business doc, so users that share a business are counted once via the
  // business — but for top-line "platform-wide" we just sum the per-business
  // values seen on `clients`.
  const totalMinutesUsed = clients.reduce((s, c) => s + (c.minutes_used || 0), 0);
  const totalSmsUsed     = clients.reduce((s, c) => s + (c.sms_used || 0), 0);
  const planCounts = { starter: 0, growth: 0, agency: 0 };
  clients.forEach(c => { if (planCounts[c.plan] !== undefined) planCounts[c.plan]++; });

  // ALL cost numbers come from backend `stats`. No hardcoded per-min/per-SMS
  // estimates — see the LAUNCH spec, this is a real-money dashboard.
  const totalCost  = stats.total_cost  ?? 0;
  const vapiCost   = stats.vapi_cost   ?? 0;
  const twilioCost = stats.twilio_cost ?? 0;
  const openaiCost = stats.openai_cost ?? 0;
  const profit     = stats.mrr - totalCost;
  const margin     = stats.mrr > 0 ? Math.round((profit / stats.mrr) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#f9f8f4]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="font-heading font-semibold text-gray-900 text-lg">NextBell</Link>
          <span className="text-[10px] bg-gray-900 text-white px-2 py-0.5 rounded-full font-medium uppercase">Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500 hidden md:block">{user?.email}</span>
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-900">Marketing Site</Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-8 py-8 space-y-8">
        {/* Tabs */}
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900">Admin Dashboard</h1>
          <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1">
            {[{id:'overview', label:'Overview'}, {id:'clients', label:'Clients'}, {id:'health', label:'Health'}, {id:'audit', label:'Audit Log'}, {id:'revenue', label:'Revenue'}].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.id ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div data-testid="admin-error-banner" className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-800">
            <strong>Admin data failed to load.</strong> {error}
          </div>
        )}

        {tab === 'overview' && (
          <>
            {/* Top KPIs — all numbers come from backend `stats`, zero estimates. */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPI icon={Users} label="Total Clients" value={stats.total} sub={`${stats.active} active`} trend="" />
              <KPI icon={DollarSign} label="Monthly Revenue (MRR)" value={`$${stats.mrr.toLocaleString()}`} sub="Recurring" trend="" up />
              <KPI icon={DollarSign} label="Est. Monthly Cost" value={`$${totalCost.toFixed(2)}`} sub="Vapi + Twilio" trend="" />
              <KPI icon={TrendingUp} label="Est. Profit" value={`$${profit.toFixed(2)}`} sub={`${margin}% margin`} trend={profit > 0 ? 'Healthy' : 'Review pricing'} up={profit > 0} />
            </div>

            {/* Usage stats — real cost surfaced under each. */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3"><Phone className="w-4 h-4 text-blue-600" /><span className="text-sm font-medium text-gray-900">Total Calls</span></div>
                <p className="text-3xl font-heading font-semibold text-gray-900">{stats.calls}</p>
                <p className="text-xs text-gray-400 mt-1">Across all clients this month</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-blue-600" /><span className="text-sm font-medium text-gray-900">Total Minutes</span></div>
                <p className="text-3xl font-heading font-semibold text-gray-900">{totalMinutesUsed.toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-1">Real Vapi cost: ${vapiCost.toFixed(2)}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3"><MessageSquare className="w-4 h-4 text-blue-600" /><span className="text-sm font-medium text-gray-900">Total SMS</span></div>
                <p className="text-3xl font-heading font-semibold text-gray-900">{totalSmsUsed.toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-1">Real Twilio cost: ${twilioCost.toFixed(2)}</p>
              </div>
            </div>

            {/* Plan Breakdown */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h3 className="font-heading text-base font-medium text-gray-900 mb-4">Revenue by Plan</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <PlanBreakdown name="Starter" count={planCounts.starter} price={297} color="bg-gray-400" />
                <PlanBreakdown name="Growth" count={planCounts.growth} price={747} color="bg-blue-600" />
                <PlanBreakdown name="Agency" count={planCounts.agency} price={1997} color="bg-purple-600" />
              </div>
            </div>

            {/* LIVE COST BREAKDOWN — the headline anti-estimate card. */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="admin-live-cost-breakdown">
              <h3 className="font-heading text-base font-medium text-gray-900">Live Cost Breakdown</h3>
              <p className="text-xs text-gray-400 mt-1 mb-4">Real costs pulled from Vapi and Twilio APIs. Updated on each page load.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500">Vapi (real total)</p>
                  <p className="text-lg font-heading font-semibold text-gray-900" data-testid="cost-tile-vapi">${vapiCost.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500">Twilio (voice+SMS+#)</p>
                  <p className="text-lg font-heading font-semibold text-gray-900" data-testid="cost-tile-twilio">${twilioCost.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500">OpenAI / LLM</p>
                  <p className="text-lg font-heading font-semibold text-gray-900" data-testid="cost-tile-openai">${openaiCost.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500">Total cost</p>
                  <p className="text-lg font-heading font-semibold text-gray-900" data-testid="cost-tile-total">${totalCost.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </>
        )}

        {tab === 'clients' && (
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-medium text-gray-900">All users ({users.length})</h2>
                <p className="text-xs text-gray-500 mt-0.5">Real per-user API spend from Vapi / Twilio / OpenAI</p>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" placeholder="Search..." className="border border-gray-200 rounded-full pl-9 pr-4 py-2 text-sm focus:border-blue-500 w-56" />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="text-left text-xs text-gray-500 uppercase px-4 py-3 font-medium">User</th>
                  <th className="text-left text-xs text-gray-500 uppercase px-4 py-3 font-medium">Business</th>
                  <th className="text-left text-xs text-gray-500 uppercase px-4 py-3 font-medium">Plan</th>
                  <th className="text-left text-xs text-gray-500 uppercase px-4 py-3 font-medium">Status</th>
                  <th className="text-right text-xs text-gray-500 uppercase px-4 py-3 font-medium">Calls</th>
                  <th className="text-right text-xs text-gray-500 uppercase px-4 py-3 font-medium">Minutes</th>
                  <th className="text-right text-xs text-gray-500 uppercase px-4 py-3 font-medium">SMS</th>
                  <th className="text-right text-xs text-gray-500 uppercase px-4 py-3 font-medium">Vapi $</th>
                  <th className="text-right text-xs text-gray-500 uppercase px-4 py-3 font-medium">Twilio $</th>
                  <th className="text-right text-xs text-gray-500 uppercase px-4 py-3 font-medium">OpenAI $</th>
                  <th className="text-right text-xs text-gray-500 uppercase px-4 py-3 font-medium">Total $</th>
                </tr></thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr><td colSpan="11" className="text-center text-gray-400 py-12 text-sm">No users yet.</td></tr>
                  ) : users.map((u) => {
                    const c = u.costs || {};
                    const twilioTotal = (c.twilio_voice || 0) + (c.twilio_sms || 0) + (c.twilio_number_monthly || 0);
                    const isOrphan = u.user_id === '__orphan__';
                    const statusLabel = isOrphan ? 'Orphan'
                      : (!u.business_id ? 'No business'
                      : (u.is_trial ? 'Trial'
                      : (u.is_active ? 'Active' : 'Off')));
                    const statusClass = isOrphan ? 'bg-gray-100 text-gray-600'
                      : (!u.business_id ? 'bg-gray-100 text-gray-600'
                      : (u.is_trial ? 'bg-amber-50 text-amber-800'
                      : (u.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700')));
                    return (
                      <tr key={u.user_id} className={`border-b border-gray-50 hover:bg-gray-50/50 ${isOrphan ? 'bg-amber-50/30' : ''}`} data-testid={`admin-user-row-${u.user_id}`}>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">{u.name || '—'}</p>
                          <p className="text-xs text-gray-400">{u.email}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{u.business_name || '—'}</td>
                        <td className="px-4 py-3">
                          {u.plan
                            ? <span className="text-xs capitalize bg-blue-50 text-blue-700 px-2 py-1 rounded-full">{u.plan}</span>
                            : <span className="text-xs text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${statusClass}`}>{statusLabel}</span></td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{c.calls_count || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{u.minutes_used || 0} / {u.minutes_cap || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{u.sms_used || 0} / {u.sms_cap || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right font-mono">${(c.vapi_total || 0).toFixed(4)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right font-mono">${twilioTotal.toFixed(4)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right font-mono">${(c.vapi_llm || 0).toFixed(4)}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right font-mono">${(u.total_cost || 0).toFixed(4)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'health' && (
          <div className="space-y-6" data-testid="admin-health-panel">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-medium text-gray-900">Tenant health</h2>
                <p className="text-xs text-gray-500 mt-0.5">Cross-tenant scan for broken provisioning, over-cap usage, and silent accounts.</p>
              </div>
              <button onClick={refreshHealth} disabled={healthLoading}
                data-testid="admin-health-refresh"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${healthLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {!health && healthLoading && <div className="text-center text-gray-400 py-12 text-sm">Scanning…</div>}
            {!health && !healthLoading && <div className="text-center text-gray-400 py-12 text-sm">Click Refresh to scan.</div>}

            {health && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <KPI icon={Users} label="Total tenants" value={health.summary.total} sub="" trend="" />
                  <KPI icon={CheckCircle2} label="Healthy" value={health.summary.healthy} sub="No issues" trend="" up />
                  <KPI icon={AlertTriangle} label="Unhealthy" value={health.summary.unhealthy} sub="≥1 issue" trend="" />
                  <KPI icon={Activity} label="Total issues" value={Object.values(health.summary.issues_by_kind || {}).reduce((a,b)=>a+b,0)} sub="" trend="" />
                </div>

                {Object.keys(health.summary.issues_by_kind || {}).length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <h3 className="font-heading text-sm font-medium text-gray-900 mb-3">Issues by kind</h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(health.summary.issues_by_kind).map(([kind, n]) => (
                        <span key={kind} className="text-xs bg-amber-50 text-amber-800 px-2.5 py-1 rounded-full border border-amber-100">
                          {kind.replace(/_/g, ' ')} · {n}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <h3 className="font-heading text-base font-medium text-gray-900">Tenants needing attention ({health.summary.unhealthy})</h3>
                  </div>
                  <table className="w-full">
                    <thead><tr className="bg-gray-50/50 border-b border-gray-100">
                      <th className="text-left text-xs text-gray-500 uppercase px-6 py-3 font-medium">Business</th>
                      <th className="text-left text-xs text-gray-500 uppercase px-6 py-3 font-medium">Plan</th>
                      <th className="text-left text-xs text-gray-500 uppercase px-6 py-3 font-medium">Phone</th>
                      <th className="text-left text-xs text-gray-500 uppercase px-6 py-3 font-medium">Issues</th>
                    </tr></thead>
                    <tbody>
                      {health.businesses.filter(b => b.issue_count > 0).length === 0 ? (
                        <tr><td colSpan="4" className="text-center text-gray-400 py-12 text-sm">All tenants healthy 🎉</td></tr>
                      ) : health.businesses.filter(b => b.issue_count > 0).map((b) => (
                        <tr key={b.business_id} className="border-b border-gray-50 align-top hover:bg-gray-50/50" data-testid={`admin-health-row-${b.business_id}`}>
                          <td className="px-6 py-4">
                            <p className="text-sm font-medium text-gray-900">{b.name || '(unnamed)'}</p>
                            <p className="text-xs text-gray-400">{b.slug || b.business_id}</p>
                          </td>
                          <td className="px-6 py-4 align-top">
                            <span className="text-xs capitalize bg-blue-50 text-blue-700 px-2 py-1 rounded-full">{b.plan || '—'}</span>
                            {b.is_trial && <span className="text-[10px] ml-1 text-amber-700">trial</span>}
                          </td>
                          <td className="px-6 py-4 align-top">
                            {b.phone_number ? <span className="text-xs text-gray-700 font-mono">{b.phone_number}</span> : <span className="text-xs text-red-600">missing</span>}
                          </td>
                          <td className="px-6 py-4">
                            <ul className="space-y-1.5">
                              {b.issues.map((it, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-xs">
                                  <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${it.severity === 'critical' ? 'bg-red-500' : it.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-400'}`} />
                                  <div>
                                    <p className="text-gray-900 font-medium">{it.message}</p>
                                    {it.fix && <p className="text-gray-500 mt-0.5">Fix: <span className="font-mono">{it.fix}</span></p>}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'audit' && (
          <div className="space-y-6" data-testid="admin-audit-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-heading text-lg font-medium text-gray-900">Audit log</h2>
                <p className="text-xs text-gray-500 mt-0.5">Privileged action history. Used for compliance + incident review.</p>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-gray-400" />
                <select value={auditKind} onChange={(e) => setAuditKind(e.target.value)}
                  data-testid="admin-audit-kind-select"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="impersonation_start">Impersonation</option>
                  <option value="support_session_start">Support session start</option>
                  <option value="support_autofix">Support auto-fix</option>
                  <option value="">All kinds</option>
                </select>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="text-left text-xs text-gray-500 uppercase px-6 py-3 font-medium">When</th>
                  <th className="text-left text-xs text-gray-500 uppercase px-6 py-3 font-medium">Kind</th>
                  <th className="text-left text-xs text-gray-500 uppercase px-6 py-3 font-medium">Admin</th>
                  <th className="text-left text-xs text-gray-500 uppercase px-6 py-3 font-medium">Target</th>
                  <th className="text-left text-xs text-gray-500 uppercase px-6 py-3 font-medium">IP / UA</th>
                </tr></thead>
                <tbody>
                  {!audit && <tr><td colSpan="5" className="text-center text-gray-400 py-12 text-sm">Loading…</td></tr>}
                  {audit && audit.rows.length === 0 && <tr><td colSpan="5" className="text-center text-gray-400 py-12 text-sm">No events.</td></tr>}
                  {audit && audit.rows.map((r) => (
                    <tr key={r.id || r.created_at} className="border-b border-gray-50 align-top hover:bg-gray-50/50">
                      <td className="px-6 py-4 text-xs text-gray-500 whitespace-nowrap">{(r.created_at || '').replace('T', ' ').slice(0, 19)}Z</td>
                      <td className="px-6 py-4">
                        <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-full font-medium">{r.kind}</span>
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <p className="text-gray-900 font-medium">{r.actor_admin_email || r.email || '—'}</p>
                        <p className="text-gray-400 font-mono">{r.actor_admin_id || ''}</p>
                      </td>
                      <td className="px-6 py-4 text-xs">
                        {r.target_business_name && <p className="text-gray-900 font-medium">{r.target_business_name}</p>}
                        {r.target_business_id && <p className="text-gray-400 font-mono">{r.target_business_id}</p>}
                        {!r.target_business_id && r.email && <p className="text-gray-700">{r.email}</p>}
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">
                        <p className="font-mono">{r.ip || '—'}</p>
                        <p className="truncate max-w-[260px]" title={r.user_agent || ''}>{r.user_agent || ''}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'revenue' && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="font-heading text-base font-medium text-gray-900 mb-4">Monthly P&amp;L</h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-50"><span className="text-sm text-gray-600">Revenue (MRR)</span><span className="text-sm font-medium text-green-700">+${stats.mrr.toLocaleString()}</span></div>
                  <div className="flex justify-between py-2 border-b border-gray-50"><span className="text-sm text-gray-600">Vapi (real)</span><span className="text-sm text-red-600">-${vapiCost.toFixed(2)}</span></div>
                  <div className="flex justify-between py-2 border-b border-gray-50"><span className="text-sm text-gray-600">Twilio (voice + SMS + #)</span><span className="text-sm text-red-600">-${twilioCost.toFixed(2)}</span></div>
                  <div className="flex justify-between py-2 border-b border-gray-50"><span className="text-sm text-gray-600">OpenAI / LLM</span><span className="text-sm text-red-600">-${openaiCost.toFixed(2)}</span></div>
                  <div className="flex justify-between py-3 border-t-2 border-gray-200"><span className="text-sm font-semibold text-gray-900">Net Profit</span><span className={`text-sm font-semibold ${profit > 0 ? 'text-green-700' : 'text-red-600'}`}>${profit.toFixed(2)}</span></div>
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="font-heading text-base font-medium text-gray-900 mb-4">Cost per Client (avg)</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Average revenue per client</p>
                    <p className="text-2xl font-heading font-semibold text-gray-900">${stats.total > 0 ? (stats.mrr / stats.total).toFixed(2) : 0}/mo</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Average cost per client</p>
                    <p className="text-2xl font-heading font-semibold text-gray-900">${stats.total > 0 ? (totalCost / stats.total).toFixed(2) : 0}/mo</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Average profit per client</p>
                    <p className="text-2xl font-heading font-semibold text-green-700">${stats.total > 0 ? (profit / stats.total).toFixed(2) : 0}/mo</p>
                  </div>
                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-400 mb-1">Profit margin</p>
                    <p className="text-2xl font-heading font-semibold text-gray-900">{margin}%</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h3 className="font-heading text-base font-medium text-gray-900">Live Cost Breakdown</h3>
              <p className="text-xs text-gray-400 mt-1 mb-4">Real costs pulled from Vapi and Twilio APIs. Updated on each page load.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-xl p-4"><p className="text-xs text-gray-500">Vapi (real total)</p><p className="text-lg font-heading font-semibold text-gray-900">${vapiCost.toFixed(2)}</p></div>
                <div className="bg-gray-50 rounded-xl p-4"><p className="text-xs text-gray-500">Twilio (voice+SMS+#)</p><p className="text-lg font-heading font-semibold text-gray-900">${twilioCost.toFixed(2)}</p></div>
                <div className="bg-gray-50 rounded-xl p-4"><p className="text-xs text-gray-500">OpenAI / LLM</p><p className="text-lg font-heading font-semibold text-gray-900">${openaiCost.toFixed(2)}</p></div>
                <div className="bg-gray-50 rounded-xl p-4"><p className="text-xs text-gray-500">Total cost</p><p className="text-lg font-heading font-semibold text-gray-900">${totalCost.toFixed(2)}</p></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, trend, up }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <Icon className="w-5 h-5 text-blue-600" />
        {trend && (
          <span className={`flex items-center gap-0.5 text-[10px] font-medium ${up ? 'text-green-600' : 'text-red-500'}`}>
            {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{trend}
          </span>
        )}
      </div>
      <p className="text-2xl font-heading font-semibold text-gray-900">{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
    </div>
  );
}

function PlanBreakdown({ name, count, price, color }) {
  return (
    <div className="text-center">
      <div className={`w-full h-2 ${color} rounded-full mb-3`} />
      <p className="text-2xl font-heading font-semibold text-gray-900">{count}</p>
      <p className="text-sm text-gray-600">{name}</p>
      <p className="text-xs text-gray-400">${(count * price).toLocaleString()}/mo revenue</p>
    </div>
  );
}
